/**
 * AdminProfitability — Real-time Profitability Dashboard.
 *
 * Primary source of truth for fleet profitability. Live-computed from
 * the same engine that powers transaction reports — values are always
 * current; no month-end reconciliation step needed.
 *
 * Backed by /api/profitability/{snapshot,timeseries,exceptions,duplicate-cards}.
 */
import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, Badge } from "../../components/ui-extras";
import { ArrowsClockwise, ChartBar, Warning, Funnel } from "@phosphor-icons/react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  LineChart, Line, PieChart, Pie, Cell,
} from "recharts";

const fmt = (n) => {
  if (n === undefined || n === null || n === "") return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const pct = (n) => (n === undefined || n === null || !Number.isFinite(Number(n)) ? "—" : `${Number(n).toFixed(2)}%`);
const COLORS = ["#0e7c66", "#d97706", "#2563eb", "#9333ea", "#dc2626", "#0891b2"];

export default function AdminProfitability() {
  const [dateFrom, setDateFrom] = useState("2026-05-04");
  const [dateTo, setDateTo] = useState("2026-05-31");
  const [filters, setFilters] = useState({ customer_id: "", network: "", card: "", province: "", product: "", currency: "" });
  const [bucket, setBucket] = useState("daily");
  const [snapshot, setSnapshot] = useState(null);
  const [series, setSeries] = useState(null);
  const [exceptions, setExceptions] = useState(null);
  const [duplicateCards, setDuplicateCards] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadAll = async () => {
    if (!dateFrom || !dateTo) return toast.error("Pick a date range");
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo, ...Object.fromEntries(Object.entries(filters).filter(([k, v]) => v)) };
      const [s, t, e, d, r] = await Promise.all([
        api.get("/profitability/snapshot", { params }),
        api.get("/profitability/timeseries", { params: { ...params, bucket } }),
        api.get("/profitability/exceptions", { params: { date_from: dateFrom, date_to: dateTo } }),
        api.get("/profitability/duplicate-cards"),
        api.get("/profitability/reconciliation", { params: { date_from: dateFrom, date_to: dateTo } }),
      ]);
      setSnapshot(s.data);
      setSeries(t.data);
      setExceptions(e.data);
      setDuplicateCards(d.data);
      setReconciliation(r.data);
    } catch (err) {
      toast.error("Failed: " + (err?.response?.data?.detail || err.message));
    }
    setLoading(false);
  };

  // Initial + bucket-change loads are explicit click-triggered to satisfy
  // React 19's set-state-in-effect rule. Dashboard renders empty state on
  // first paint; user clicks Apply to populate.
  useEffect(() => {
    if (snapshot) {
      // Bucket toggle: refetch only the timeseries, not the full snapshot.
      const params = { date_from: dateFrom, date_to: dateTo, bucket, ...Object.fromEntries(Object.entries(filters).filter(([k, v]) => v)) };
      api.get("/profitability/timeseries", { params })
        .then((r) => setSeries(r.data))
        .catch((err) => toast.error("Series refresh failed: " + (err?.response?.data?.detail || err.message)));
    }
  }, [bucket]);

  const t = snapshot?.totals;

  // Build "Fuel GP vs DEF GP" pie data
  const fuelVsDef = snapshot ? [
    { name: "Fuel GP", value: snapshot.totals.fuel_gp_cad },
    { name: "DEF GP",  value: snapshot.totals.def_gp_cad },
  ] : [];

  return (
    <div className="space-y-6" data-testid="profitability-dashboard">
      <PageHeader
        title="Real-Time Profitability"
        subtitle="Live engine — always current. Reconciliation panel is for audit only."
      />

      {/* Filter bar */}
      <div className="rounded border bg-white p-3">
        <div className="mb-2 text-xs uppercase tracking-wider text-zinc-500"><Funnel className="-mt-0.5 mr-1 inline" />Filters</div>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-5 lg:grid-cols-9">
          <FilterField label="From" type="date" value={dateFrom} onChange={setDateFrom} testid="prof-from" />
          <FilterField label="To" type="date" value={dateTo} onChange={setDateTo} testid="prof-to" />
          <FilterField label="Customer ID" value={filters.customer_id} onChange={(v) => setFilters({ ...filters, customer_id: v })} testid="prof-customer" />
          <FilterField label="Network" value={filters.network} onChange={(v) => setFilters({ ...filters, network: v })} placeholder="esso/pipeline/petro_pass" testid="prof-network" />
          <FilterField label="Card" value={filters.card} onChange={(v) => setFilters({ ...filters, card: v })} testid="prof-card" />
          <FilterField label="Province" value={filters.province} onChange={(v) => setFilters({ ...filters, province: v })} placeholder="ON, AB, BC…" testid="prof-province" />
          <FilterField label="Product" value={filters.product} onChange={(v) => setFilters({ ...filters, product: v })} placeholder="DIESEL_LS, DEFD…" testid="prof-product" />
          <FilterField label="Currency" value={filters.currency} onChange={(v) => setFilters({ ...filters, currency: v })} placeholder="CAD/USD" testid="prof-currency" />
          <div className="flex items-end gap-2">
            <button onClick={loadAll} disabled={loading} className="rounded bg-black px-3 py-2 text-sm font-medium text-white disabled:opacity-50" data-testid="prof-apply-btn">
              {loading ? <ArrowsClockwise className="inline animate-spin" /> : "Apply"}
            </button>
          </div>
        </div>
      </div>

      {/* KPI tiles */}
      {t && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-8" data-testid="prof-tiles">
          <Tile label="Fuel GP (CAD eq.)" value={fmt(t.fuel_gp_cad)} sub={`Raw: ${fmt(t.fuel_gp)}`} primary />
          <Tile label="DEF GP (CAD eq.)" value={fmt(t.def_gp_cad)} sub={`Raw: ${fmt(t.def_gp)}`} />
          <Tile label="Combined GP (CAD eq.)" value={fmt(t.combined_gp_cad)} />
          <Tile label="Revenue" value={fmt(t.revenue)} />
          <Tile label="Cost" value={fmt(t.cost)} />
          <Tile label="Margin %" value={pct(t.margin_pct)} />
          <Tile label="Margin / Litre" value={t.margin_per_unit ? `$${Number(t.margin_per_unit).toFixed(4)}` : "—"} />
          <Tile label="Rows" value={(snapshot.row_count || 0).toLocaleString()} sub={`${dateFrom} → ${dateTo}`} />
        </div>
      )}

      {/* Time series with bucket selector */}
      {series && (
        <div className="rounded border bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm font-semibold"><ChartBar className="-mt-0.5 mr-1 inline" />GP Over Time</div>
            <div className="flex gap-1" data-testid="prof-bucket-switcher">
              {["daily","weekly","monthly","ytd"].map((b) => (
                <button key={b} onClick={() => setBucket(b)} className={`rounded border px-2 py-1 text-xs ${bucket===b?"border-black bg-black text-white":"border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50"}`} data-testid={`prof-bucket-${b}`}>{b}</button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={series.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="key" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="fuel_gp_cad" stroke="#0e7c66" strokeWidth={2} name="Fuel GP (CAD)" />
              <Line type="monotone" dataKey="def_gp_cad"  stroke="#d97706" strokeWidth={2} name="DEF GP (CAD)" />
              <Line type="monotone" dataKey="combined_gp_cad" stroke="#2563eb" strokeWidth={2} name="Combined (CAD)" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Side-by-side: customer / network breakdowns + fuel vs DEF pie */}
      {snapshot && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Panel title="By Network" testid="prof-by-network">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={snapshot.by_network} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis dataKey="label" type="category" tick={{ fontSize: 11 }} width={80} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Bar dataKey="combined_gp_cad" name="Combined GP (CAD)" fill="#0e7c66" />
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="Fuel vs DEF (CAD eq.)" testid="prof-fuel-vs-def">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={fuelVsDef} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                  {fuelVsDef.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="Top Customers (CAD eq.)" testid="prof-top-customers">
            <div className="max-h-56 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50"><tr><th className="p-2 text-left">Customer</th><th className="p-2 text-right">Combined</th><th className="p-2 text-right">Margin %</th></tr></thead>
                <tbody>
                  {snapshot.by_customer.slice(0, 10).map((c) => (
                    <tr key={c.key} className="border-t"><td className="p-2">{c.label}</td><td className="p-2 text-right font-mono">{fmt(c.combined_gp_cad)}</td><td className="p-2 text-right font-mono">{pct(c.margin_pct)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      )}

      {/* Exceptions queue */}
      {exceptions && (
        <Panel title={`Exceptions Queue (${exceptions.exception_count})`} rightLabel={`Scanned: ${exceptions.scanned}`} testid="prof-exceptions">
          {exceptions.exception_count === 0 ? (
            <div className="rounded bg-emerald-50 p-3 text-sm text-emerald-800">No exceptions detected — every row has a customer, FX rate, margin, tax rule, and product mapping.</div>
          ) : (
            <>
              <div className="mb-2 flex gap-2 text-xs">
                {Object.entries(exceptions.by_issue).map(([k, v]) => (
                  <Badge key={k} variant="warning"><Warning className="-mt-0.5 mr-1 inline" />{k}: {v}</Badge>
                ))}
              </div>
              <div className="max-h-72 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50"><tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Card</th><th className="p-2 text-left">Customer</th><th className="p-2 text-left">Product</th><th className="p-2 text-left">Prov</th><th className="p-2 text-right">GP</th><th className="p-2 text-left">Issues</th></tr></thead>
                  <tbody>
                    {exceptions.exceptions.map((e, i) => (
                      <tr key={i} className="border-t bg-amber-50/40">
                        <td className="p-2 font-mono">{e.tran_date}</td>
                        <td className="p-2 font-mono">{e.card_number}</td>
                        <td className="p-2">{e.customer_name || "(unmatched)"}</td>
                        <td className="p-2">{e.product}</td>
                        <td className="p-2">{e.province}</td>
                        <td className="p-2 text-right font-mono">{fmt(e.gp)}</td>
                        <td className="p-2 text-amber-700">{e.issues.join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Panel>
      )}

      {/* Reconciliation Audit — Legacy GP vs Billed Profit per network bucket.
          Flags any bucket >3% variance. Operator-approved 2026-06-13. */}
      {reconciliation && <ReconciliationPanel data={reconciliation} />}

      {/* Duplicate cards */}
      {duplicateCards && (
        <Panel title={`Duplicate Cards (${duplicateCards.duplicate_count})`} testid="prof-dupe-cards">
          {duplicateCards.duplicate_count === 0 ? (
            <div className="rounded bg-emerald-50 p-3 text-sm text-emerald-800">No duplicate card registrations found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-50"><tr><th className="p-2 text-left">Card #</th><th className="p-2 text-left">Networks</th><th className="p-2 text-right">Owners</th><th className="p-2 text-left">Customer Names</th><th className="p-2 text-left">Notes</th></tr></thead>
              <tbody>
                {duplicateCards.duplicates.map((d) => (
                  <tr key={d.card_number} className="border-t bg-amber-50/30">
                    <td className="p-2 font-mono">{d.card_number}</td>
                    <td className="p-2">{d.networks.join(", ")}</td>
                    <td className="p-2 text-right font-mono">{d.count}</td>
                    <td className="p-2">{d.customer_names.join(" · ")}</td>
                    <td className="p-2 text-xs text-zinc-600">{d.notes.join(" · ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}
    </div>
  );
}

function FilterField({ label, value, onChange, type, placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-zinc-500">{label}</label>
      <input type={type || "text"} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
             className="mt-1 w-full rounded border px-2 py-1.5 font-mono text-xs" data-testid={testid} />
    </div>
  );
}
function Tile({ label, value, sub, primary }) {
  return (
    <div className={`rounded border bg-white p-3 ${primary ? "border-emerald-300" : ""}`} data-testid={`prof-tile-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`mt-1 font-mono text-lg ${primary ? "text-emerald-700" : "text-zinc-900"}`}>{value}</div>
      {sub && <div className="font-mono text-xs text-zinc-500">{sub}</div>}
    </div>
  );
}
function Panel({ title, rightLabel, children, testid }) {
  return (
    <div className="rounded border bg-white" data-testid={testid}>
      <div className="flex items-center justify-between border-b bg-zinc-50 px-3 py-2">
        <div className="text-sm font-semibold text-zinc-700">{title}</div>
        {rightLabel && <div className="text-xs text-zinc-500">{rightLabel}</div>}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Reconciliation Audit panel — per-bucket Legacy GP vs Billed Profit
// (no-tax / with-tax). Flags any bucket whose no-tax variance > 3 %.

function ReconciliationPanel({ data }) {
  const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const pct = (n) => (n === null || n === undefined) ? "—" : `${Number(n).toFixed(2)}%`;
  const anyFlagged = data.buckets.some((b) => b.flagged) || data.total.flagged;
  return (
    <Panel
      title={`Reconciliation Audit — Legacy GP vs Billed Profit (threshold ${data.variance_threshold_pct}%)`}
      rightLabel={`${data.date_from} → ${data.date_to}`}
      testid="prof-reconciliation"
    >
      <div className={`mb-3 rounded p-2 text-xs ${anyFlagged ? "bg-red-50 text-red-700 border border-red-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`} data-testid="prof-reconciliation-banner">
        {anyFlagged
          ? "⚠ One or more network buckets exceed the ±3 % variance threshold. Investigate before promoting billed profit to the headline."
          : "✓ All network buckets within ±3 %. Billed profit reconciles to Legacy GP modulo expected sales-tax-on-margin uplift."}
      </div>
      <div className="overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50">
            <tr>
              <th className="p-2 text-left">Bucket</th>
              <th className="p-2 text-right">Rows</th>
              <th className="p-2 text-right">Legacy GP</th>
              <th className="p-2 text-right">Billed P (no tax)</th>
              <th className="p-2 text-right">Billed P (with tax)</th>
              <th className="p-2 text-right">Δ vs Legacy</th>
              <th className="p-2 text-right">Δ %</th>
              <th className="p-2 text-right">Sales-tax uplift</th>
              <th className="p-2 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.buckets.map((b) => (
              <tr key={b.label} className={`border-t ${b.flagged ? "bg-red-50/40" : ""}`} data-testid={`prof-reconciliation-row-${b.label.replace(/[^a-z0-9]+/gi, '_').toLowerCase()}`}>
                <td className="p-2 font-medium">{b.label}</td>
                <td className="p-2 text-right font-mono">{(b.rows || 0).toLocaleString()}</td>
                <td className="p-2 text-right font-mono">{fmt(b.legacy_gp)}</td>
                <td className="p-2 text-right font-mono">{fmt(b.bp_no_tax)}</td>
                <td className="p-2 text-right font-mono font-semibold text-emerald-700">{fmt(b.bp_with_tax)}</td>
                <td className={`p-2 text-right font-mono ${b.variance_no_tax_vs_legacy_abs > 0 ? "text-emerald-700" : b.variance_no_tax_vs_legacy_abs < 0 ? "text-red-700" : ""}`}>{fmt(b.variance_no_tax_vs_legacy_abs)}</td>
                <td className={`p-2 text-right font-mono ${b.flagged ? "text-red-700 font-semibold" : ""}`}>{pct(b.variance_no_tax_vs_legacy_pct)}</td>
                <td className="p-2 text-right font-mono text-zinc-600">{fmt(b.sales_tax_uplift_on_margin)}</td>
                <td className="p-2 text-center">
                  {b.rows === 0 ? <span className="text-zinc-400">—</span>
                   : b.flagged ? <span className="text-red-700 font-semibold">FLAGGED</span>
                   : <span className="text-emerald-700">OK</span>}
                </td>
              </tr>
            ))}
            <tr className="border-t-2 border-zinc-300 bg-zinc-50 font-semibold">
              <td className="p-2">{data.total.label}</td>
              <td className="p-2 text-right font-mono">{(data.total.rows || 0).toLocaleString()}</td>
              <td className="p-2 text-right font-mono">{fmt(data.total.legacy_gp)}</td>
              <td className="p-2 text-right font-mono">{fmt(data.total.bp_no_tax)}</td>
              <td className="p-2 text-right font-mono text-emerald-700">{fmt(data.total.bp_with_tax)}</td>
              <td className={`p-2 text-right font-mono ${data.total.variance_no_tax_vs_legacy_abs > 0 ? "text-emerald-700" : data.total.variance_no_tax_vs_legacy_abs < 0 ? "text-red-700" : ""}`}>{fmt(data.total.variance_no_tax_vs_legacy_abs)}</td>
              <td className={`p-2 text-right font-mono ${data.total.flagged ? "text-red-700" : ""}`}>{pct(data.total.variance_no_tax_vs_legacy_pct)}</td>
              <td className="p-2 text-right font-mono text-zinc-600">{fmt(data.total.sales_tax_uplift_on_margin)}</td>
              <td className="p-2 text-center">{data.total.flagged ? <span className="text-red-700">FLAGGED</span> : <span className="text-emerald-700">OK</span>}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[11px] text-zinc-500">
        Billed Profit (no tax) should reconcile to Legacy GP within sales-tax uplift. The Δ% column flags any structural data-shape drift. Pipeline branch fix verified 2026-06-13.
      </div>
    </Panel>
  );
}
